import "./ProgressSection.css"

import { Col, OverlayTrigger, Tooltip } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { PiUsersThree } from "react-icons/pi"

import Loading from "@/components/Loading/Loading"
import NoMessage from "@/components/noMessage/NoMessage"
import { useStudentProgressData } from "@/queries/queries"

import ProgressBar from "./ProgressBar"

const firstRowLTR = -0
const SecondRowLTR = -50

const firstColumnLTR = -10
const secondColumnLTR = -70
const thirdColumnLTR = -130

const badgePositionsLTR = [
  [firstColumnLTR, firstRowLTR],
  [secondColumnLTR, firstRowLTR],
  [thirdColumnLTR, firstRowLTR],
  [firstColumnLTR, SecondRowLTR],
  [secondColumnLTR, SecondRowLTR],
  [thirdColumnLTR, SecondRowLTR],
]

const Badge = ({ pos = 0 }) => {
  const [horizontal, vertical] = badgePositionsLTR[pos]

  return (
    <div
      style={{
        width: "60px",
        height: "50px",
        overflow: "hidden",
      }}
    >
      <img
        style={{
          width: "200px",
          height: "200px",
          objectFit: "contain",
          objectPosition: `${horizontal}px ${vertical}px`,
        }}
        src="assets/images/badges.png"
      />
    </div>
  )
}

export default function YourProgress({ id }: { id?: number }) {
  const studentProgressQuery = useStudentProgressData()

  const { t } = useTranslation()

  const badges: Array<React.ReactNode> = [
    <Badge pos={0} key={0} />,
    <Badge pos={1} key={1} />,
    <Badge pos={2} key={2} />,
    <Badge pos={3} key={3} />,
    <Badge pos={4} key={4} />,
    <Badge pos={5} key={5} />,
  ]
  const badgeNames = [
    t("homePage.leaderboard.progress.badgeNames.bronze"),
    t("homePage.leaderboard.progress.badgeNames.silver"),
    t("homePage.leaderboard.progress.badgeNames.gold"),
    t("homePage.leaderboard.progress.badgeNames.platinum"),
    t("homePage.leaderboard.progress.badgeNames.diamond"),
    t("homePage.leaderboard.progress.badgeNames.topOne"),
  ]

  if (studentProgressQuery.isLoading) {
    return <Loading />
  }

  if (!studentProgressQuery.data?.length) {
    return <NoMessage name={t("noMessages.data")} />
  }

  return (
    <div className="py-5 border-bottom position-relative soon-wrapper">
      <h5>{t("homePage.leaderboard.progress.title")}</h5>

      <div className="row main-con">
        {studentProgressQuery.data
          ?.filter((el) => Number(el?.round?.id) === id)
          .map((ele, index) => {
            const progress = ele?.total_progress
              ? Math.min(100, ele.total_progress)
              : 0

            const { isUnder50, currentBadge, nextBadgeName } = getBadgeProgress(
              progress,
              badges,
              badgeNames
            )

            return (
              <Col dir="ltr" key={index} lg={12} className="mb-4">
                <div className="sub-con position-relative">
                  {/* ✅ Overlay لو أقل من 50 */}
                  {isUnder50 && (
                    <div className="overlay-message">
                      <div className="overlay-text">
                        {t("homePage.leaderboard.progress.progressUnder50")}
                      </div>
                    </div>
                  )}

                  <div className="d-flex align-items-center gap-3 mb-4 fw-bold">
                    <div className="icon-con">
                      <PiUsersThree size={24} />
                    </div>
                    <p className="m-0">{ele?.round?.name}</p>
                  </div>

                  <div className="d-flex align-items-center gap-3 w-100">
                    <div>{currentBadge}</div>
                    {/* <div>test</div> */}
                    <ProgressBar
                      value={progress > 50 ? (progress - 50) * 2 : 0}
                      currentProgress={progress}
                      nextBadgeName={nextBadgeName}
                    />
                    <p className="m-0 title__name">{nextBadgeName}</p>
                  </div>

                  <div className="d-flex justify-content-between mt-3 rank-con">
                    {badges.map((badge, badgeIndex) => {
                      const isActive =
                        badges.indexOf(currentBadge) >= badgeIndex

                      return (
                        <OverlayTrigger
                          key={badgeIndex}
                          placement="bottom"
                          overlay={
                            <Tooltip id={`tooltip-${badgeIndex}`}>
                              <div>
                                <strong>{badgeNames[badgeIndex]}</strong>
                                <div>
                                  <strong>{badgeIndex * 10 + 50}%</strong>
                                </div>
                              </div>
                            </Tooltip>
                          }
                        >
                          <span
                            className={`reward ${isActive ? "active" : ""}`}
                          >
                            {badge}
                          </span>
                        </OverlayTrigger>
                      )
                    })}
                  </div>
                </div>
              </Col>
            )
          })}
      </div>
    </div>
  )
}

/*
    bronze: 50 - 60
    silver: 60 - 70
    gold: 70 - 80 
    paltinum: 80 - 90
    diamond: 90 - 99
    Top one: 100
  */

function getBadgeProgress(
  progress: number,
  badges: Array<React.ReactNode>,
  badgeNames: Array<string>
): {
  isUnder50: boolean
  currentBadge: React.ReactNode
  nextBadgeName: string
} {
  if (typeof progress !== "number" || progress < 0 || progress > 100) {
    console.warn("Invalid progress value:", progress)
    return {
      isUnder50: true,
      currentBadge: null,
      nextBadgeName: "",
    }
  }

  // ✅ لو أقل من 50%
  const isUnder50 = progress < 50

  if (isUnder50) {
    return {
      isUnder50,
      currentBadge: badges[0], // Bronze كبداية
      nextBadgeName: badgeNames[1], // Silver
    }
  }

  // ✅ لو 50% أو أكتر → نبدأ الحساب النسبي
  const adjustedProgress = progress - 50
  const thresholds = [0, 10, 20, 30, 40, 50]

  for (let i = 0; i < thresholds.length - 1; i++) {
    if (
      adjustedProgress >= thresholds[i] &&
      adjustedProgress < thresholds[i + 1]
    ) {
      return {
        isUnder50,
        currentBadge: badges[i],
        nextBadgeName: badgeNames[i + 1] || "",
      }
    }
  }

  return {
    isUnder50,
    currentBadge: badges[badges.length - 1],
    nextBadgeName: "100%",
  }
}
